import React, { createContext, useContext, useState, useEffect } from 'react';

const modes = ['light', 'dark'] as const;
const accents = ['none', 'green', 'purple', 'blue'] as const;
const fontSizes = [0, 1, 2] as const; // Small, Medium, Large indices

type Mode = typeof modes[number];
type Accent = typeof accents[number];
type FontSizeIndex = typeof fontSizes[number];
type ThemeString =
  | 'light-none'
  | 'light-green'
  | 'light-purple'
  | 'light-blue'
  | 'dark-none'
  | 'dark-green'
  | 'dark-purple'
  | 'dark-blue';

// Font size values
const fontSizeValues = ["0.92rem", "1rem", "1.12rem"]; // Small, Medium, Large
const fontSizeLabels = ["Small", "Medium", "Large"];

// Local storage keys
const STORAGE_KEYS = {
  MODE: 'theme-mode',
  ACCENT: 'theme-accent',
  FONT_SIZE: 'theme-font-size',
  USER_HAS_PREFERENCE: 'theme-user-preference'
} as const;

interface ThemeContextType {
  theme: ThemeString;
  setTheme: (theme: ThemeString) => void;
  mode: Mode;
  setMode: (mode: Mode) => void;
  accent: Accent;
  setAccent: (accent: Accent) => void;
  fontSize: FontSizeIndex;
  setFontSize: (size: FontSizeIndex) => void;
  isUsingSystemTheme: boolean;
  fontSizeLabels: string[];
  modes: Mode[];
  accents: Accent[];
}

// Cookie utility functions for enterprise environments where localStorage is cleared
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
}

// Utility functions for localStorage with cookie fallback
function getStoredValue<T>(key: string, defaultValue: T): T {
  try {
    // Try localStorage first
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
    
    // Fallback to cookie
    const cookieValue = getCookie(key);
    if (cookieValue) {
      try {
        return JSON.parse(cookieValue);
      } catch {
        return defaultValue;
      }
    }
    
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function setStoredValue<T>(key: string, value: T): void {
  try {
    const stringValue = JSON.stringify(value);
    // Save to both localStorage and cookie
    localStorage.setItem(key, stringValue);
    setCookie(key, stringValue);
  } catch (error) {
    console.warn('Failed to save to storage:', error);
  }
}

// Detect system theme preference
function getSystemTheme(): Mode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light-none',
  setTheme: () => {},
  mode: 'light',
  setMode: () => {},
  accent: 'none',
  setAccent: () => {},
  fontSize: 1,
  setFontSize: () => {},
  isUsingSystemTheme: true,
  fontSizeLabels,
  modes: modes.slice() as Mode[],
  accents: accents.slice() as Accent[],
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Check if user has made any theme choices before
  const [hasUserPreference, setHasUserPreference] = useState(
    () => getStoredValue(STORAGE_KEYS.USER_HAS_PREFERENCE, false)
  );

  // Initialize with system theme if no user preference, otherwise use stored values
  const [mode, setModeState] = useState<Mode>(() => {
    if (hasUserPreference) {
      return getStoredValue(STORAGE_KEYS.MODE, 'light');
    }
    return getSystemTheme();
  });

  const [accent, setAccentState] = useState<Accent>(() => 
    getStoredValue(STORAGE_KEYS.ACCENT, 'none')
  );

  const [fontSize, setFontSizeState] = useState<FontSizeIndex>(() => 
    getStoredValue(STORAGE_KEYS.FONT_SIZE, 1)
  );

  const theme: ThemeString = `${mode}-${accent}` as ThemeString;

  // Listen for system theme changes when user hasn't set a preference
  useEffect(() => {
    if (!hasUserPreference && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setModeState(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [hasUserPreference]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Apply font size to document
  useEffect(() => {
    document.documentElement.style.setProperty('--app-font-size', fontSizeValues[fontSize]);
  }, [fontSize]);

  // Enhanced setters that save to localStorage
  const setMode = (newMode: Mode) => {
    setModeState(newMode);
    setStoredValue(STORAGE_KEYS.MODE, newMode);
    if (!hasUserPreference) {
      setHasUserPreference(true);
      setStoredValue(STORAGE_KEYS.USER_HAS_PREFERENCE, true);
    }
  };

  const setAccent = (newAccent: Accent) => {
    setAccentState(newAccent);
    setStoredValue(STORAGE_KEYS.ACCENT, newAccent);
    if (!hasUserPreference) {
      setHasUserPreference(true);
      setStoredValue(STORAGE_KEYS.USER_HAS_PREFERENCE, true);
    }
  };

  const setFontSize = (newFontSize: FontSizeIndex) => {
    setFontSizeState(newFontSize);
    setStoredValue(STORAGE_KEYS.FONT_SIZE, newFontSize);
  };

  const setTheme = (newTheme: ThemeString) => {
    const [newMode, newAccent] = newTheme.split('-') as [Mode, Accent];
    setMode(newMode);
    setAccent(newAccent);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        mode,
        setMode,
        accent,
        setAccent,
        fontSize,
        setFontSize,
        isUsingSystemTheme: !hasUserPreference,
        fontSizeLabels,
        modes: modes.slice() as Mode[],
        accents: accents.slice() as Accent[],
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};